import { api } from './http';
import type {
  Batch, BatchInput, Enquiry, EnquiryStatus, EnquiryTopic, MarketEvent, MarketEventInput,
  Notification, NotificationInput, NotificationSend, SendSummary, Placement, PlacementInput,
  Faq, FaqInput, Testimonial, TestimonialInput,
} from './types';

export const batches = {
  list: (signal?: AbortSignal) => api.get<{ items: Batch[] }>('/batches', signal),
  get: (id: number) => api.get<{ item: Batch }>(`/batches/${id}`),
  create: (f: BatchInput) => api.post<{ item: Batch }>('/batches', f),
  update: (id: number, f: BatchInput) => api.put<{ item: Batch }>(`/batches/${id}`, f),
  remove: (id: number) => api.del<{ ok: true }>(`/batches/${id}`),
};

export const placements = {
  list: (signal?: AbortSignal) => api.get<{ items: Placement[] }>('/placements', signal),
  create: (f: PlacementInput) => api.post<{ item: Placement }>('/placements', f),
  update: (id: number, f: PlacementInput) => api.put<{ item: Placement }>(`/placements/${id}`, f),
  remove: (id: number) => api.del<{ ok: true }>(`/placements/${id}`),
};

export const notifications = {
  list: (signal?: AbortSignal) => api.get<{
    items: Notification[]; sends: Record<number, SendSummary>; configured: boolean;
  }>('/notifications', signal),
  /** Every attempt to push one notice, newest first, with who it would reach now. */
  sends: (id: number, signal?: AbortSignal) => api.get<{
    notification: Notification; sends: NotificationSend[];
    audience: { devices: number; members: number; held: number }; configured: boolean;
  }>(`/notifications/${id}/sends`, signal),
  send: (id: number) => api.post<{ send: NotificationSend; already: boolean }>(`/notifications/${id}/send`),
  create: (f: NotificationInput) => api.post<{ item: Notification }>('/notifications', f),
  update: (id: number, f: NotificationInput) => api.put<{ item: Notification }>(`/notifications/${id}`, f),
  remove: (id: number) => api.del<{ ok: true }>(`/notifications/${id}`),
};

export const enquiries = {
  list: (signal?: AbortSignal) => api.get<{
    enquiries: Enquiry[];
    counts: Record<string, number>;
    subscribers: { id: number; email: string; created_at: string }[];
  }>('/enquiries', signal),
  update: (id: number, f: { status: EnquiryStatus; topic: EnquiryTopic; reply_note: string }) =>
    api.put<{ enquiry: Enquiry }>(`/enquiries/${id}`, f),
  remove: (id: number) => api.del<{ ok: true }>(`/enquiries/${id}`),
};

export type LemonStatus = 'pending' | 'approved' | 'rejected' | 'revoked';
export type LemonRequest = {
  id: number; member_id: number; name: string; email: string; mobile: string;
  lemon_id: string; telegram: string; status: LemonStatus; note: string;
  decided_by: string; decided_at: string | null; telegram_added_at: string | null;
  access_until: string | null; granted_access: number; revoked_at: string | null; created_at: string;
};

export const lemon = {
  list: (signal?: AbortSignal) => api.get<{ requests: LemonRequest[]; pending: number; days: number }>('/lemon', signal),
  act: (id: number, f: { decision: 'approved' | 'rejected' | 'revoked' | 'telegram'; note?: string; added?: boolean }) =>
    api.put<{ request: LemonRequest }>(`/lemon/${id}`, f),
  setDays: (days: number) => api.put<{ days: number }>('/lemon/settings', { days }),
};

export const faqs = {
  list: (signal?: AbortSignal) => api.get<{ items: Faq[] }>('/faqs', signal),
  create: (f: FaqInput) => api.post<{ item: Faq }>('/faqs', f),
  update: (id: number, f: FaqInput) => api.put<{ item: Faq }>(`/faqs/${id}`, f),
  remove: (id: number) => api.del<{ ok: true }>(`/faqs/${id}`),
};

export const marketEvents = {
  list: (signal?: AbortSignal) => api.get<{ items: MarketEvent[] }>('/market-events', signal),
  create: (f: MarketEventInput) => api.post<{ item: MarketEvent }>('/market-events', f),
  update: (id: number, f: MarketEventInput) => api.put<{ item: MarketEvent }>(`/market-events/${id}`, f),
  remove: (id: number) => api.del<{ ok: true }>(`/market-events/${id}`),
};

export const testimonials = {
  list: (signal?: AbortSignal) => api.get<{ items: Testimonial[] }>('/testimonials', signal),
  create: (f: TestimonialInput) => api.post<{ item: Testimonial }>('/testimonials', f),
  update: (id: number, f: TestimonialInput) => api.put<{ item: Testimonial }>(`/testimonials/${id}`, f),
  remove: (id: number) => api.del<{ ok: true }>(`/testimonials/${id}`),
};
